<template>
    <div class="mb-3">
        <!-- Status: Voucher code cancelled -->
        <div v-if="voucher.cancelled" class="avp-voucher-status avp-voucher-cancelled">
            <p class="card-text">{{ $t('voucherStatus.supporting_text.generic.status.cancelled.text') }}</p>
            <CustomerServiceBlock/>
        </div>

        <!-- Status: Voucher code is already expired -->
        <div v-else-if="voucher.expired" class="avp-voucher-status avp-voucher-expired">
            <p class="card-text">{{ $t('voucherStatus.supporting_text.generic.status.expired.text') }}</p>
            <CustomerServiceBlock/>
        </div>

        <!-- Status: Voucher code is valid but personal data is needed -->
        <div v-else-if="showOrderFinishForm" class="row pb-md-2">
            <div class="col-12 text-center">
                <h4>
                    {{ $t('bookingService.order_mode.text') }}
                    <br/>
                    {{ $t('bookingService.order_mode.text2') }}
                    <span class="tooltip" v-if="tooltipVisible">
                        Sie haben Ihren Kauf nicht bei uns getätigt. Wir müssen daher sicherstellen, dass Sie das korrekte Produkt erhalten. Dies dient Ihrer Sicherheit.
                    </span>
                    <InfoIcon class="info-icon" @mouseover="showTooltip" @mouseout="hideTooltip"/>
                </h4>
            </div>
            <div class="col-12 pt-3">
                <OrderDetails :data="orderDetails" />
                <p>
                    Falsches Produkt? Wenden Sie sich an unseren Kundensupport: <a href="tel:+4922193374100">0221 - 933 74 100</a>
                </p>
            </div>
            <div class="col-12">
                <FinishOrderForm  @submit="finishOrderProcess" :is-sending="isSendingData"/>
                <small>
                    Ich habe die <a href="https://www.animod.de/c/agb" target="_blank">AGB</a> und die <a href="https://www.animod.de/c/datenschutz" target="_blank">Datenschutzerklärung</a> von Animod gelesen und akzeptiere diese. Zusätzlich wird
                    Ihre E-Mail-Adresse für unseren Newsletter verwendet, von dem Sie sich selbstverständlich
                    jederzeit kostenfrei abmelden können.
                </small>
            </div>
        </div>

        <div v-else-if="voucher.personalDataRequired" class="avp-voucher-status avp-voucher-">
            <p class="card-text">{{ $t('voucherStatus.supporting_text.generic.status.personalDataRequired.text') }}</p>

            <ContactForm @submit="savePersonalData" :is-sending="isSending" :showNewsletterCheckbox="true" :sendVoucher="true"/>

            <CustomerServiceBlock/>
        </div>

        <!-- Multi Voucher -->
        <div v-else-if="voucher.externalVoucherCode !== null && voucher.externalVoucherCode !== ''"
             class="avp-external-valuevoucher">
            <p class="card-text">
                {{ $t('voucherStatus.supporting_text.external_valuevoucher.next_step.redeem.text') }}
                <i18n path="voucherStatus.supporting_text.external_valuevoucher.external_voucher_code.text" tag="p"
                      class="card-text">
                    <mark place="external_voucher_code">{{ voucher.externalVoucherCode }}</mark>
                </i18n>
            </p>
            <div class="card-navigation">
                <a :href="codeRedeemUrl" class="btn btn-primary">
                    {{ $t('voucherStatus.action.multivoucher.submit.text') }}
                </a>
            </div>
            <external-incentive-block :incentive-data="externalIncentive" :code="voucherCode"/>
            <CustomerServiceBlock/>
        </div>

        <!-- Multi Voucher / Access Code -->
        <div v-else-if="voucher.multiVoucher || voucher.accessCode" class="avp-multivoucher">
            <!-- Status: Voucher code already consumed -->
            <div v-if="voucher.consumed" class="avp-voucher-status avp-voucher-consumed">
                <p class="card-text">
                    {{ $t('voucherStatus.supporting_text.multivoucher.status.consumed.text') }}
                </p>
                <external-incentive-block :incentive-data="externalIncentive" :code="voucherCode"/>
                <CustomerServiceBlock/>
            </div>

            <!-- Status: Voucher code valid and not yet consumed -->
            <div v-else class="avp-voucher-status avp-voucher-valid">
                <p class="card-text" v-if="voucher.servicesExpired">
                    {{ $t('voucherStatus.supporting_text.multivoucher.servicesExpired.text') }}
                </p>
                <p class="card-text" v-else-if="voucher.accessCode">
                    {{ $t('voucherStatus.supporting_text.accesscode.next_step.redeem.text') }}
                </p>
                <p class="card-text" v-if="!voucher.redemptionPlatformAccessible">
                    {{ $t('voucherStatus.supporting_text.multivoucher.platformRetired.text') }}
                </p>
                <p class="card-text" v-else>
                    {{ $t('voucherStatus.supporting_text.multivoucher.next_step.redeem.text') }}
                </p>

              <transition name="fade">
                <h4 v-if="isOrderMode">Vielen Dank!</h4>
                <ContactForm v-if="showVoucherInfoForm"
                             :submit-text="$t('sendVoucherInfo.action.generic.submit.text')"
                             :is-sending="isSendingData"
                             :showNewsletterCheckbox="true"
                             :sendVoucher="true"
                             @submit="sendVoucherInfo">
                  <template v-slot:before>
                    <p class="card-text">
                      {{ $t('sendVoucherInfo.supporting_text.common.form_intro') }}
                    </p>
                  </template>

                  <template v-slot:after>
                    <GdprNotice/>
                  </template>
                </ContactForm>
              </transition>

                <div class="card-navigation">
                    <a :href="codeRedeemUrl"  class="btn btn-primary">
                        {{ $t('voucherStatus.action.multivoucher.submit.text') }}
                    </a>
                </div>
                <external-incentive-block :incentive-data="externalIncentive" :code="voucherCode"/>
                <CustomerServiceBlock/>
            </div>
        </div>

        <!-- Value Voucher -->
        <div v-else class="avp-voucher-valuevoucher">
            <!-- Status: Voucher code already consumed -->
            <div v-if="voucher.consumed" class="avp-voucher-status avp-voucher-consumed">
                <p class="card-text">{{ $t('voucherStatus.supporting_text.valuevoucher.status.consumed.text') }}</p>
                <p class="card-text">{{ $t('voucherStatus.supporting_text.valuevoucher.next_step.buy_new.text') }}</p>
                <p class="text-center">
                    <a :href="valuevoucherBuyNewUrl" class="btn btn-primary">{{ $t('common.to_animod_shop') }}</a>
                </p>
                <external-incentive-block :incentive-data="externalIncentive" :code="voucherCode"/>
                <CustomerServiceBlock/>
            </div>

            <!-- Status: Voucher code valid and not yet consumed -->
            <div v-else class="avp-voucher-status avp-voucher-valid">
                <p class="card-text">
                    {{ $t('voucherStatus.supporting_text.valuevoucher.status.remaining_value.text', { 'remaining_value': voucher.remainingAmountFormatted }) }}
                </p>
                <p class="card-text">
                    {{ $t('voucherStatus.supporting_text.valuevoucher.next_step.redeem.text') }}
                </p>
              <transition name="fade">
                <ContactForm v-if="showVoucherInfoForm"
                             :submit-text="$t('sendVoucherInfo.action.generic.submit.text')"
                             :is-sending="isSendingData"
                             :showNewsletterCheckbox="true"
                             :sendVoucher="true"
                             @submit="sendVoucherInfo">
                  <template v-slot:before>
                    <p class="card-text">
                      {{ $t('sendVoucherInfo.supporting_text.common.form_intro') }}
                    </p>
                  </template>

                  <template v-slot:after>
                    <GdprNotice/>
                  </template>
                </ContactForm>
              </transition>
                <div class="card-navigation">
                    <button @click="redeemValueVoucher" class="btn btn-primary">
                        {{ $t('voucherStatus.action.valuevoucher.link_redeem.text') }}
                    </button>
                    <!-- a :href=valuevoucherRedeemUrl class="btn btn-primary">{{$t( 'action.valuevoucher.link_redeem.text' )}}</a -->
                </div>
                <external-incentive-block :incentive-data="externalIncentive" :code="voucherCode"/>
                <CustomerServiceBlock/>
            </div>

        </div>
    </div>
</template>

<script>
import CustomerServiceBlock from './CustomerServiceBlock'
import CustomerServiceEmail from './CustomerServiceEmail'
import CustomerServicePhone from './CustomerServicePhone'
import ErrorMessages from './ErrorMessages';
import ExternalIncentiveBlock from './ExternalIncentiveBlock';
import GdprNotice from '@/components/GdprNotice'
import { mapState, mapGetters } from 'vuex';
import ContactForm from '@/components/ContactForm';
import OrderDetails from "@/components/OrderDetails.vue";
import InfoIcon from "../../../animod-shop-ssr/src/assets/icons/info.svg";
import FinishOrderForm from "@/components/FinishOrderForm.vue";

export default {
    name: 'ValueVoucherStatusView',
    components: {
        FinishOrderForm,
        InfoIcon,
        OrderDetails, ContactForm, CustomerServiceBlock, CustomerServiceEmail, CustomerServicePhone, ErrorMessages, ExternalIncentiveBlock, GdprNotice },
    data () {
        return {
            tooltipVisible: false,
            isSending: false,
            errors: [],
            isVoucherInfoFormSubmitted: false,
            isSendingData: false,
            firstname: '',
            lastname: '',
            email: '',
            placeholder_firstname: this.$i18n.t('generic.firstname'),
            placeholder_lastname: this.$i18n.t('generic.lastname'),
            placeholder_email: this.$i18n.t('generic.email'),
            valuevoucherRedeemUrl: this.$i18n.t('voucherStatus.action.valuevoucher.link_redeem.url'),
            valuevoucherBuyNewUrl: this.$i18n.t('voucherStatus.action.valuevoucher.link_buy_new.url')
        }
    },
    computed: {
        ...mapState(['voucher']),
        ...mapGetters(['voucherCode']),
        codeRedeemUrl() {
            if (this.voucher.landingPageUrl && !this.voucher.servicesExpired) {
                if (~this.voucher.landingPageUrl.indexOf(this.voucherCode)) {
                    return this.voucher.landingPageUrl;
                }
                else if (~this.voucher.landingPageUrl.indexOf('CODE')) {
                    return this.voucher.landingPageUrl.replace('CODE', this.voucherCode)
                } else {
                    return this.voucher.landingPageUrl + '?vv=' + this.voucherCode
                }
            } else {
                return this.$i18n.t('voucherStatus.action.multivoucher.submit.url')
            }
        },
        showVoucherInfoForm () {
          return !this.isVoucherInfoFormSubmitted
                    && this.$store.state.voucher.voucherPortalNewsletterMode !== 'LastOrderStep';
        },
        externalIncentive() {
            return this.voucher.externalIncentiveInfo;
        },
        orderDetails() {
            return this.$store.state.voucher.productInformation;
        },
        showOrderFinishForm() {
            return this.$store.state.voucher.voucherPortalNewsletterMode === 'LastOrderStep'
                && this.$store.state.voucher.personalDataRequired
                && this.$store.state.voucher.productInformation;
        },
        isOrderMode() {
            return this.$store.state.voucher.voucherPortalNewsletterMode === 'LastOrderStep';
        },
    },
    methods: {
        showTooltip() {
            this.tooltipVisible = true;
        },
        hideTooltip() {
            this.tooltipVisible = false;
        },
        redeemValueVoucher () {
            location.href = this.$i18n.t('voucherStatus.action.valuevoucher.link_redeem.url') + '?vv=' + this.voucherCode
        },
        savePersonalData (contactData) {
          try {
            this.isSending = true
            this.$store.dispatch('savePersonalData', {
              ...contactData,
              voucherCode: this.voucherCode
            });
            this.isVoucherInfoFormSubmitted = true
          } finally {
            this.isSending = false
          }
        },
        async finishOrderProcess (contactData) {
            console.log("finishOrderProcess", contactData);
            this.isSendingData = true
            try {
                const voucherCode = this.$store.getters.voucherCode;
                const response = await this.$store.dispatch('finishOrderProcess', {
                    ...contactData,
                    voucherCode,
                    sendVoucher: true
                })
                this.dataIsSent = true
            } finally {
                this.isSendingData = false
            }
        },
        async sendVoucherInfo (contactData) {
          try {
            this.isSendingData = true
            const {successful} = await this.$store.dispatch('sendVoucherDataRequest', {
              ...contactData,
              voucherCode: this.voucherCode
            })
            //this.isVoucherInfoFormSubmitted = successful @AG: please have a look - successful is undefined. Using 'true' now as a workaround
            this.isVoucherInfoFormSubmitted = true
          } finally {
            this.isSendingData = false
          }
        }
    }
}
</script>

<style scoped lang="scss">
.avp-contact-link {
    white-space: nowrap;
}

.avp-voucher-status {
    margin-bottom: 1.25rem;
}

.status-highlight {
    background-color: rgba(#fff, 0.5);
    padding: 0 0.3125rem;
}

.avp-form {
    .avp-form-button-row {
        padding: 0 5px;
        justify-content: center;

        button {
            margin: 0 .375rem;
        }

        @media (max-width: 575px) {
            flex-wrap: wrap-reverse;
        }
    }
}

h4 {
    color: #525252;
    display: block;
    font-size: 1.5rem;
    margin: 0.625rem auto;
    text-align: center;
}
.tooltip-container {
    display:inline-block;
    width:20px;
    height:20px;
}
.info-icon {
    display:inline-block;
    width:20px;
    height:20px;
    color:#525252;
    fill:#525252;
}
.tooltip {
    color: #000;
    background-color: #fff;
    will-change: transform;
    top: -60px;
    left: 240px;
    position: absolute;
    border-radius: 2px;
    padding: 6px 10px;
    box-shadow: 0px 0px 3px rgba(#666, 0.8);
    max-width: 320px;
    z-index: 100;
    display:block;
    opacity: 1;
}
</style>
